import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Lightbulb, Send, Loader2, Building2, Building, Calendar, FileText,
  CheckCircle, AlertCircle, Info
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONTRIBUTION_TYPES = [
  { value: "new_intelligence", label: "New Intelligence", description: "Share new info about an entity", credits: "8-20" },
  { value: "correction", label: "Correction", description: "Fix incorrect information", credits: "5-10" },
  { value: "confirmation", label: "Confirmation", description: "Confirm existing info is accurate", credits: "1-5" }
];

const CONFIDENCE_LEVELS = [
  { value: "high", label: "High - Direct experience" },
  { value: "medium", label: "Medium - Reliable source" },
  { value: "low", label: "Low - Heard through network" }
];

const SOURCE_TYPES = [
  { value: "first_hand", label: "First-hand - I met/spoke with them directly" },
  { value: "second_hand", label: "Second-hand - From someone who did" },
  { value: "public", label: "Public - From public sources/events" }
];

function InvestorForm({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>VC Firm Name *</Label>
          <Input 
            value={data.firm_name || ""} 
            onChange={(e) => onChange({ ...data, firm_name: e.target.value })}
            placeholder="e.g., Syncona"
          />
        </div>
        <div className="space-y-2">
          <Label>Contact Person</Label>
          <Input 
            value={data.person_name || ""} 
            onChange={(e) => onChange({ ...data, person_name: e.target.value })}
            placeholder="e.g., John Smith, Partner"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Interaction Date</Label>
          <Input 
            type="month"
            value={data.interaction_date || ""} 
            onChange={(e) => onChange({ ...data, interaction_date: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Outcome</Label>
          <Select value={data.outcome || ""} onValueChange={(v) => onChange({ ...data, outcome: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Select outcome..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="meeting">Had meeting</SelectItem>
              <SelectItem value="passed">They passed</SelectItem>
              <SelectItem value="term_sheet">Term sheet received</SelectItem>
              <SelectItem value="invested">They invested</SelectItem>
              <SelectItem value="ghosted">Ghosted/No response</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Thesis Fit</Label>
          <Select value={data.thesis_fit || ""} onValueChange={(v) => onChange({ ...data, thesis_fit: v })}>
            <SelectTrigger>
              <SelectValue placeholder="How well did you fit?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="strong">Strong - Clear alignment</SelectItem>
              <SelectItem value="moderate">Moderate - Some fit</SelectItem>
              <SelectItem value="weak">Weak - Marginal fit</SelectItem>
              <SelectItem value="none">None - Outside their thesis</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Deployment Signal</Label>
          <Select value={data.deployment_signal || ""} onValueChange={(v) => onChange({ ...data, deployment_signal: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Are they actively investing?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Actively deploying</SelectItem>
              <SelectItem value="cautious">Cautious/Selective</SelectItem>
              <SelectItem value="paused">Paused investments</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {data.outcome === "passed" && (
        <div className="space-y-2">
          <Label>Pass Reason (valuable intel!)</Label>
          <Textarea 
            value={data.pass_reason || ""} 
            onChange={(e) => onChange({ ...data, pass_reason: e.target.value })}
            placeholder="Why did they pass? e.g., 'Too early stage', 'Portfolio conflict with X'"
            rows={2}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label>Additional Notes</Label>
        <Textarea 
          value={data.notes || ""} 
          onChange={(e) => onChange({ ...data, notes: e.target.value })}
          placeholder="Any other useful intel? Concerns they raised, tips for approaching them..."
          rows={3}
        />
      </div>
    </div>
  );
}

function PharmaForm({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Company Name *</Label>
          <Input 
            value={data.company_name || ""} 
            onChange={(e) => onChange({ ...data, company_name: e.target.value })}
            placeholder="e.g., Pfizer"
          />
        </div>
        <div className="space-y-2">
          <Label>Contact Name</Label>
          <Input 
            value={data.contact_name || ""} 
            onChange={(e) => onChange({ ...data, contact_name: e.target.value })}
            placeholder="e.g., Jane Doe, BD Director"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Indication Interest</Label>
          <Input 
            value={data.indication_interest || ""} 
            onChange={(e) => onChange({ ...data, indication_interest: e.target.value })}
            placeholder="e.g., Oncology, Rare diseases"
          />
        </div>
        <div className="space-y-2">
          <Label>Deal Type Interest</Label>
          <Select value={data.deal_type_interest || ""} onValueChange={(v) => onChange({ ...data, deal_type_interest: v })}>
            <SelectTrigger>
              <SelectValue placeholder="What type of deal?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="license">Licensing deal</SelectItem>
              <SelectItem value="acquisition">Acquisition</SelectItem>
              <SelectItem value="collaboration">R&D Collaboration</SelectItem>
              <SelectItem value="option">Option to license</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Key Requirements/Interests</Label>
        <Textarea 
          value={data.key_requirements || ""} 
          onChange={(e) => onChange({ ...data, key_requirements: e.target.value })}
          placeholder="What are they looking for? Stage preference, specific modalities..."
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label>Additional Notes</Label>
        <Textarea 
          value={data.notes || ""} 
          onChange={(e) => onChange({ ...data, notes: e.target.value })}
          placeholder="Any other useful intel?"
          rows={2}
        />
      </div>
    </div>
  );
}

function EventForm({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Event Name *</Label>
          <Input 
            value={data.event_name || ""} 
            onChange={(e) => onChange({ ...data, event_name: e.target.value })}
            placeholder="e.g., BIO Europe"
          />
        </div>
        <div className="space-y-2">
          <Label>Event Date</Label>
          <Input 
            type="month"
            value={data.event_date || ""} 
            onChange={(e) => onChange({ ...data, event_date: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Investor Presence</Label>
          <Select value={data.investor_presence || ""} onValueChange={(v) => onChange({ ...data, investor_presence: v })}>
            <SelectTrigger>
              <SelectValue placeholder="How many investors?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High - Many top VCs</SelectItem>
              <SelectItem value="moderate">Moderate - Some presence</SelectItem>
              <SelectItem value="low">Low - Few investors</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Pharma Presence</Label>
          <Select value={data.pharma_presence || ""} onValueChange={(v) => onChange({ ...data, pharma_presence: v })}>
            <SelectTrigger>
              <SelectValue placeholder="How many pharma?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High - Major pharma present</SelectItem>
              <SelectItem value="moderate">Moderate - Some BD teams</SelectItem>
              <SelectItem value="low">Low - Few pharma</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Networking Quality</Label>
        <Select value={data.networking_quality || ""} onValueChange={(v) => onChange({ ...data, networking_quality: v })}>
          <SelectTrigger>
            <SelectValue placeholder="Rate the networking..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="excellent">Excellent - Easy to connect</SelectItem>
            <SelectItem value="good">Good - Decent opportunities</SelectItem>
            <SelectItem value="poor">Poor - Hard to network</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Tips for Attendees</Label>
        <Textarea 
          value={data.tips || ""} 
          onChange={(e) => onChange({ ...data, tips: e.target.value })}
          placeholder="Any tips for getting the most out of this event?"
          rows={3}
        />
      </div>
    </div>
  );
}

function GrantForm({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Grant Name *</Label>
          <Input 
            value={data.grant_name || ""} 
            onChange={(e) => onChange({ ...data, grant_name: e.target.value })}
            placeholder="e.g., Innovate UK Smart Grant"
          />
        </div>
        <div className="space-y-2">
          <Label>Funder</Label>
          <Input 
            value={data.funder || ""} 
            onChange={(e) => onChange({ ...data, funder: e.target.value })}
            placeholder="e.g., UKRI, Wellcome Trust"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Deadline Update</Label>
        <Input 
          value={data.deadline_update || ""} 
          onChange={(e) => onChange({ ...data, deadline_update: e.target.value })}
          placeholder="e.g., 'Next round opens March 2026'"
        />
      </div>

      <div className="space-y-2">
        <Label>Application Tips</Label>
        <Textarea 
          value={data.application_tips || ""} 
          onChange={(e) => onChange({ ...data, application_tips: e.target.value })}
          placeholder="What worked for you? What do reviewers look for?"
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label>Success Factors</Label>
        <Textarea 
          value={data.success_factors || ""} 
          onChange={(e) => onChange({ ...data, success_factors: e.target.value })}
          placeholder="What makes a winning application?"
          rows={2}
        />
      </div>
    </div>
  );
}

export default function ContributionModal({ 
  open, 
  onClose, 
  category = "investor", // investor, pharma, event, grant
  entityId = null,
  entityName = null,
  onSuccess 
}) {
  const [contributionType, setContributionType] = useState("new_intelligence");
  const [confidenceLevel, setConfidenceLevel] = useState("medium");
  const [sourceType, setSourceType] = useState("first_hand");
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const categoryConfig = {
    investor: { icon: Building2, label: "Investor", color: "text-teal-600" },
    pharma: { icon: Building, label: "Pharma", color: "text-indigo-600" },
    event: { icon: Calendar, label: "Event", color: "text-blue-600" },
    grant: { icon: FileText, label: "Grant", color: "text-purple-600" }
  };

  const config = categoryConfig[category] || categoryConfig.investor;
  const Icon = config.icon;

  const handleSubmit = async () => {
    // Basic validation
    const requiredField = {
      investor: "firm_name",
      pharma: "company_name",
      event: "event_name",
      grant: "grant_name"
    }[category];

    if (!formData[requiredField]) {
      toast.error(`Please enter the ${requiredField.replace("_", " ")}`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        category,
        contribution_type: contributionType,
        entity_id: entityId,
        data: {
          ...formData,
          confidence_level: confidenceLevel,
          source_type: sourceType
        }
      };

      const response = await fetch(`${BACKEND_URL}/api/contributions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        onSuccess?.(result);
        onClose();
        // Reset form
        setFormData({});
        setContributionType("new_intelligence");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit contribution");
      }
    } catch (error) {
      toast.error("Failed to submit contribution");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${config.color}`} />
            Share {config.label} Intelligence
            {entityName && (
              <Badge variant="secondary" className="ml-2">{entityName}</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Help the community with your insights. Earn credits when your contribution is validated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Contribution Type */}
          <div className="space-y-3">
            <Label>Type of Contribution</Label>
            <div className="grid grid-cols-3 gap-3">
              {CONTRIBUTION_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setContributionType(type.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    contributionType === type.value
                      ? "border-teal-500 bg-teal-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="font-medium text-sm">{type.label}</div>
                  <div className="text-xs text-slate-500">{type.credits} credits</div>
                </button>
              ))}
            </div>
          </div>

          {/* Source & Confidence */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Source Type</Label>
              <Select value={sourceType} onValueChange={setSourceType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_TYPES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Confidence Level</Label>
              <Select value={confidenceLevel} onValueChange={setConfidenceLevel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONFIDENCE_LEVELS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Category-specific form */}
          <div className="border-t pt-4">
            {category === "investor" && <InvestorForm data={formData} onChange={setFormData} />}
            {category === "pharma" && <PharmaForm data={formData} onChange={setFormData} />}
            {category === "event" && <EventForm data={formData} onChange={setFormData} />}
            {category === "grant" && <GrantForm data={formData} onChange={setFormData} />}
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <strong>How credits work:</strong> Your contribution will be reviewed by our team. 
              Once validated, credits are added to your account. 
              Reach 50 validated credits to unlock the discounted £39.50/mo subscription rate!
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting}
            className="bg-teal-600 hover:bg-teal-700"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Intelligence
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
